import { useContext, useState, useEffect, useRef, useCallback, useMemo } from "react"
import { css } from "@emotion/react"
import useRawPromise from "../../hooks/useRawPromise"
import Loader from "../_general/Loader"
import FancyDropdown from "../_control/FancyDropdown"
import PitchListContext from "../../context/PitchList"
import { useIntl } from "react-intl"
import useRawState from "../../hooks/useRawState"

export default function MultiplePitchSelection({ selectedPitches, setSelectedPitches, maxPitches = 30, ...props }) {

    const { get } = useRawPromise()
    const { pitches, sortAlphabetical } = useContext(PitchListContext)
    const { formatMessage } = useIntl()

    const { data: venuesData } = useRawState('/api/current/venues', { pagination: false })
    const venues = useMemo(() => {
        return venuesData?.toSorted((a, b) => a.name.localeCompare(b.name)) || []
    }, [venuesData])

    const [venueAddLoading, setVenueAddLoading] = useState(false)

    const handleAddVenuePitches = useCallback(async (venueId) => {
        setVenueAddLoading(true)
        const { data: venuePitches } = await get(`/api/current/venues/${venueId}/pitches?pagination=false`)
        if (!venuePitches) return
        if (!venuePitches || venuePitches.length === 0) return setVenueAddLoading(false)
        for (const pitch of venuePitches) {
            const id = pitch.id
            if (selectedPitches.find(p => p.value === id)) continue
            setSelectedPitches(curr => [...curr, { value: id, label: pitch.name, pitchSortRank: pitch.pitchSortRank }])
        }
        setVenueAddLoading(false)
    }, [venues, selectedPitches])

    // only resort the list if length changes
    const lengthRef = useRef(selectedPitches.length)
    useEffect(() => {
        const lastLength = lengthRef.current
        if (selectedPitches.length > maxPitches) return setSelectedPitches(current => current.filter(p => current.indexOf(p) < maxPitches))

        const sort = sortAlphabetical ?
            (a, b) => a.label.localeCompare(b.label)
            : (a, b) => a.pitchSortRank - b.pitchSortRank

        const newLength = selectedPitches.length
        lengthRef.current = newLength
        if (lastLength !== newLength) {
            setSelectedPitches(current => current.toSorted(sort))
        }

    }, [selectedPitches, sortAlphabetical])

    const isMulti = maxPitches > 1

    return <div css={css`display: grid; gap: 1em;`}>
        <h2 css={css`text-transform: uppercase;`}>
            {
                maxPitches === 1 ?
                    formatMessage({ id: 'sportsField' })
                    : formatMessage({ id: 'sportsFields' })
            }
            &nbsp;
            {selectedPitches.length > 0 && isMulti && `(${selectedPitches.length})`}
            {selectedPitches.length === maxPitches && isMulti && ` - ${formatMessage({ id: 'limitReached' })}`}
        </h2>
        <FancyDropdown
            options={pitches.map(p => ({
                value: p.id,
                label: p.name,
                pitchSortRank: p.pitchSortRank,
            }))}
            value={selectedPitches}
            onChange={isMulti ?
                (values) => setSelectedPitches(values)
                :
                (value) => setSelectedPitches([value])
            }
            isClearable={true}
            isMulti={isMulti}
            placeholder={formatMessage({ id: 'selectEllipses' })}
        />
        {isMulti &&
            <>
                <h2 css={css` text-transform: uppercase; `}>
                    <div css={css`display: flex; gap: 1em;`}>
                        {formatMessage({ id: 'addPitchesFromVenue' })}
                        {venueAddLoading &&
                            <Loader size={'1em'} />
                        }
                    </div>
                </h2>
                <FancyDropdown
                    options={venues.map(v => ({
                        value: v.id,
                        label: v.name,
                    }))}
                    value={undefined}
                    onChange={(val) => handleAddVenuePitches(val.value)}
                    isClearable={false}
                    placeholder={formatMessage({ id: 'selectEllipses' })}
                />

            </>
        }
    </div>
}
